import {Component, Input, OnInit} from '@angular/core';
import {EcommerceConnection} from "../../../../model/EcommerceConnection";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {EcommerceConnectionService} from "../../../service-data/ecommerce-connection.service";

@Component({
  selector: 'app-ecommerce-connection-status',
  templateUrl: './ecommerce-connection-status.component.html',
  styleUrls: ['./ecommerce-connection-status.component.css']
})
export class EcommerceConnectionStatusComponent {
  @Input() ecommerceConnection: EcommerceConnection;

  daysString = LanguagePipe.get('days', 'compressSpaces');
  hoursString = LanguagePipe.get('hours', 'compressSpaces');
  minutesString = LanguagePipe.get('minutes ago', 'compressSpaces');
  minuteString = LanguagePipe.get('minute ago', 'compressSpaces');

  lastConnectionUpdating: boolean = false;
  lastOrderCheckUpdating: boolean = false;
  lastProductReadUpdating: boolean = false;
  lastOrderProcessedUpdating: boolean = false;
  lastProductProcessedUpdating: boolean = false;
  lastProductImagesProcessedUpdating: boolean = false;
  lastShipmentProcessedUpdating: boolean = false;

  constructor(public ecommerceConnectionService: EcommerceConnectionService) {
  }

  /**
   * Does this connection poll the store for new orders?
   *
   * A status value only means something if the pipeline behind it runs.
   * On a connection that does not poll, "Orders Read" can only ever
   * show `INITIAL` plus "No activity recorded" -- a question-mark icon
   * next to the words "no activity", which is indistinguishable from a
   * feature that is broken or was never set up. Operators chase those.
   * The opposite case is worse: a status the server left at its last
   * real value sits there as a permanent stale `ERROR` for work nobody
   * has asked to be done since.
   *
   * The rows are not removed when they do not apply. A missing row
   * raises "why is there no shipment status here?", which is the same
   * question in a different costume. The row stays and says, in words,
   * what is not happening -- which also tells the operator which
   * setting to go and change.
   */
  get pollsForOrders(): boolean {
    return this.isEnabled(this.ecommerceConnection ? this.ecommerceConnection.pollForRemoteOrders : undefined);
  }

  /** Does this connection poll the store for its product catalog? */
  get pollsForProducts(): boolean {
    return this.isEnabled(this.ecommerceConnection ? this.ecommerceConnection.pollForRemoteProducts : undefined);
  }

  /** Does this connection push our product content out to the store? */
  get processesProducts(): boolean {
    return this.isEnabled(this.ecommerceConnection ? this.ecommerceConnection.synchronizeProducts : undefined);
  }

  /**
   * Shipment reporting covers two separate things the server does once
   * a package ships: push the tracking number, and set the order's
   * state in the store. `remoteOrderState` can be set while
   * `synchronizeShipments` is off, and in that case the row still
   * reports real work -- so it is relevant if EITHER is in play.
   */
  get reportsShipments(): boolean {
    if ( this.isEnabled(this.ecommerceConnection ? this.ecommerceConnection.synchronizeShipments : undefined) ) {
      return true;
    }
    const remoteOrderState = this.ecommerceConnection ? this.ecommerceConnection.remoteOrderState : '';
    return !!remoteOrderState && String(remoteOrderState).trim() !== '';
  }

  /**
   * `undefined` means "the payload did not say", which has to read as
   * ON. These columns were added after the connections that use them,
   * so a payload that predates one of them must not blank out a panel
   * of status rows -- failing closed here would hide features that are
   * working.
   */
  private isEnabled(value: boolean | undefined | null): boolean {
    return value === undefined || value === null ? true : !!value;
  }

  updateStatus(item: any): void {
    item.updating = true;
    // Simulate an update request (replace with your actual logic)
    setTimeout(() => {
      item.updating = false;
      item.timestamp = new Date();
      item.status = Math.random() < 0.8 ? 'success' : Math.random() < 0.5 ? 'failure' : 'warning';
    }, 2000);
  }

  getTimeAgo(date: Date): string {
    if ( !date || date.getTime() < 946770000000 ) { // If the date is earlier than 2000 then return ---
      return '---';
    }
    const now = new Date();
    const x = date.getTime();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}\u00A0${this.daysString}, ${hours % 24}\u00A0${this.hoursString}, ${minutes % 60}\u00A0${minutes % 60 === 1 ? this.minuteString : this.minutesString}`;
    } else if (hours > 0) {
      return `${hours}\u00A0${this.hoursString}, ${minutes % 60}\u00A0${this.minutesString}`;
    } else {
      return `${minutes}\u00A0${minutes % 60 === 1 ? this.minuteString : this.minutesString}`;
    }
  }

  checkConnection(ecommerceConnection: EcommerceConnection) {
    this.lastConnectionUpdating = true;
    this.ecommerceConnectionService.checkConnection(ecommerceConnection).subscribe( result => {
      this.lastConnectionUpdating = false;
    });
  }

  readOrders(ecommerceConnection: EcommerceConnection) {
    this.lastOrderProcessedUpdating = true;
    this.ecommerceConnectionService.readOrders(ecommerceConnection).subscribe( result => {
      this.lastOrderProcessedUpdating = false;
    });
  }

  processOrders(ecommerceConnection: EcommerceConnection) {
    this.lastProductReadUpdating = true;
    this.ecommerceConnectionService.processOrders(ecommerceConnection).subscribe( result => {
      this.lastProductReadUpdating = false;
    });
  }

  readProducts(ecommerceConnection: EcommerceConnection) {
    this.lastProductReadUpdating = true;
    this.ecommerceConnectionService.readProducts(ecommerceConnection).subscribe( result => {
      this.lastProductReadUpdating = false;
    });
  }


  sendProducts(ecommerceConnection: EcommerceConnection) {
    this.lastProductProcessedUpdating = true;
    this.ecommerceConnectionService.sendProducts(ecommerceConnection).subscribe( result => {
      this.lastProductProcessedUpdating = false;
    });
  }

  sendShipmentStatus(ecommerceConnection: EcommerceConnection) {
    this.lastShipmentProcessedUpdating = true;
    this.ecommerceConnectionService.sendShipmentStatus(ecommerceConnection).subscribe( result => {
      this.lastShipmentProcessedUpdating = false;
    });
  }
}

